import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getCreditCardIndex } from '../services/dataService';
import { CreditCardIndex } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import { useCountry } from '../context/CountryContext';

const CreditCard: React.FC<{ card: CreditCardIndex }> = ({ card }) => (
    <Link to={`/credit-card-charges/${card.slug}`} className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 ease-in-out transform hover:-translate-y-1 border border-gray-200">
        <div>
            <h3 className="font-semibold text-gray-800">{card.name}</h3>
        </div>
        <svg className="w-5 h-5 text-gray-400 ml-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
    </Link>
);

const CreditCardChargesPage: React.FC = () => {
    const { country } = useCountry();
    const [cards, setCards] = useState<CreditCardIndex[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        getCreditCardIndex(country).then(data => {
            setCards(data);
            setLoading(false);
        });
    }, [country]);

    const filteredCards = useMemo(() => {
        return cards.filter(card =>
            card.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [cards, searchTerm]);

    return (
        <div className="bg-gray-50 py-12">
            <div className="container mx-auto px-6 max-w-4xl">
                <header className="text-center mb-10">
                    <h1 className="text-4xl font-extrabold font-poppins tracking-tight text-gray-900 sm:text-5xl">Credit Card Fees</h1>
                    <p className="mt-4 text-xl text-gray-600">Find details on annual fees, late payment charges, and more.</p>
                </header>
                
                <div className="relative mb-8">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>
                    </div>
                    <input
                        type="search"
                        className="block w-full py-3 pl-12 pr-4 text-gray-900 border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:ring-[#246BFD] focus:border-[#246BFD] text-base"
                        placeholder="Search for a credit card..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {loading ? <LoadingSpinner /> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {filteredCards.map(card => (
                            <CreditCard key={card.id} card={card} />
                        ))}
                    </div>
                )}
                 {filteredCards.length === 0 && !loading && (
                    <div className="text-center py-10 px-6 bg-white rounded-lg shadow-sm border">
                        <h2 className="text-xl font-medium text-gray-800">No Cards Found</h2>
                        <p className="text-gray-500 mt-2">Your search for "{searchTerm}" did not match any credit cards.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreditCardChargesPage;
